import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import SalesOrderService, { SalesOrder, SalesOrderItem } from '../../services/sales-order.service';
import CustomerService from '../../services/customer.service';
import ProductService from '../../services/product.service';
import QuotationService from '../../services/quotation.service';
import { NumberTextField } from '../../components/common';

interface LineItem {
  tempId: string;
  id?: number;
  product: any;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  tax: number;
  total: number;
}

const SalesOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [customers, setCustomers] = useState<any[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  
  const [quotations, setQuotations] = useState<any[]>([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState<any>(null);
  
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const [formData, setFormData] = useState<SalesOrder>({
    orderNumber: `SO-${Date.now()}`,
    customer: 0,
    orderDate: new Date().toISOString().split('T')[0],
    deliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days
    subtotal: 0,
    tax: 0,
    discount: 0,
    total: 0,
    status: 'pending',
    notes: '',
  });

  useEffect(() => {
    fetchCustomers();
    fetchQuotations();
    fetchProducts();
    if (isEdit && id) {
      fetchSalesOrder(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await CustomerService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setCustomers(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchQuotations = async () => {
    try {
      setQuotationsLoading(true);
      const response = await QuotationService.getAll({
        'filters[status][$eq]': 'accepted',
        'pagination[pageSize]': 100,
        populate: 'customer',
      });
      setQuotations(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setQuotationsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await ProductService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
        populate: 'category',
      });
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchSalesOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await SalesOrderService.getOne(orderId);
      const orderData = response.data?.data;

      if (orderData) {
        const attrs = orderData.attributes;
        setFormData({
          orderNumber: attrs.orderNumber,
          customer: attrs.customer?.data?.id || 0,
          quotation: attrs.quotation?.data?.id,
          orderDate: attrs.orderDate,
          deliveryDate: attrs.deliveryDate || '',
          subtotal: attrs.subtotal || 0,
          tax: attrs.tax || 0,
          discount: attrs.discount || 0,
          total: attrs.total || 0,
          status: attrs.status || 'pending',
          notes: attrs.notes || '',
        });

        if (attrs.customer?.data) {
          setSelectedCustomer(attrs.customer.data);
        }

        if (attrs.quotation?.data) {
          setSelectedQuotation(attrs.quotation.data);
        }

        // Fetch line items
        const itemsResponse = await SalesOrderService.getItems(orderId);
        const items = itemsResponse.data?.data || [];
        
        const mappedItems: LineItem[] = items.map((item: any) => ({
          tempId: `item-${item.id}`,
          id: item.id,
          product: item.attributes?.product?.data,
          description: item.attributes?.description || '',
          quantity: item.attributes?.quantity || 1,
          unitPrice: item.attributes?.unitPrice || 0,
          discount: item.attributes?.discount || 0,
          tax: item.attributes?.tax || 0,
          total: item.attributes?.total || 0,
        }));
        
        setLineItems(mappedItems);
      }
    } catch (error) {
      console.error('Error fetching sales order:', error);
      setError('Failed to load sales order');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCustomerChange = (event: any, newValue: any) => {
    setSelectedCustomer(newValue);
    setFormData({ ...formData, customer: newValue?.id || 0 });
  };

  const handleQuotationChange = async (event: any, newValue: any) => {
    setSelectedQuotation(newValue);
    setFormData({ ...formData, quotation: newValue?.id });

    // Load quotation items if a quotation is selected
    if (newValue?.id) {
      try {
        const itemsResponse = await QuotationService.getItems(newValue.id);
        const items = itemsResponse.data?.data || [];
        
        const mappedItems: LineItem[] = items.map((item: any) => ({
          tempId: `temp-${Date.now()}-${Math.random()}`,
          product: item.attributes?.product?.data,
          description: item.attributes?.description || '',
          quantity: item.attributes?.quantity || 1,
          unitPrice: item.attributes?.unitPrice || 0,
          discount: item.attributes?.discount || 0,
          tax: item.attributes?.tax || 0,
          total: item.attributes?.total || 0,
        }));
        
        setLineItems(mappedItems);
      } catch (error) {
        console.error('Error loading quotation items:', error);
      }
    }
  };

  const addLineItem = () => {
    const newItem: LineItem = {
      tempId: `temp-${Date.now()}`,
      product: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 0,
      total: 0,
    };
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((item) => item.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItem, value: any) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.tempId === tempId) {
          const updated = { ...item, [field]: value };
          
          // Auto-fill price when product is selected
          if (field === 'product' && value) {
            updated.unitPrice = Number(value.attributes?.price) || 0;
            updated.description = value.attributes?.name || '';
          }
          
          // Convert numeric fields to numbers
          if (field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'tax') {
            updated[field] = Number(value) || 0;
          }
          
          // Calculate line total
          const qty = Number(updated.quantity) || 0;
          const price = Number(updated.unitPrice) || 0;
          const discount = Number(updated.discount) || 0;
          const tax = Number(updated.tax) || 0;
          
          const subtotal = qty * price;
          const afterDiscount = subtotal - discount;
          updated.total = afterDiscount + tax;
          
          return updated;
        }
        return item;
      })
    );
  };

  // Calculate order totals
  useEffect(() => {
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = Number(item.quantity) * Number(item.unitPrice);
      return sum + itemSubtotal;
    }, 0);

    const totalDiscount = lineItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
    const totalTax = lineItems.reduce((sum, item) => sum + (Number(item.tax) || 0), 0);
    const total = subtotal - totalDiscount + totalTax;

    setFormData((prev) => ({
      ...prev,
      subtotal: Number(subtotal) || 0,
      discount: Number(totalDiscount) || 0,
      tax: Number(totalTax) || 0,
      total: Number(total) || 0,
    }));
  }, [lineItems]);

  const validateForm = (): boolean => {
    if (!formData.customer) {
      setError('Please select a customer');
      return false;
    }
    if (lineItems.length === 0) {
      setError('Please add at least one line item');
      return false;
    }
    if (lineItems.some(item => !item.product)) {
      setError('All line items must have a product selected');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let orderId: number;

      // Create or update sales order
      if (isEdit && id) {
        await SalesOrderService.update(parseInt(id), formData);
        orderId = parseInt(id);
        setSuccess('Sales Order updated!');
      } else {
        const response = await SalesOrderService.create(formData);
        orderId = response.data?.data?.id;
        setSuccess('Sales Order created!');
      }

      // Save line items
      if (orderId) {
        // Delete old items if editing
        if (isEdit) {
          const oldItemsResponse = await SalesOrderService.getItems(orderId);
          const oldItems = oldItemsResponse.data?.data || [];
          await Promise.all(
            oldItems.map((item: any) => SalesOrderService.deleteItem(item.id))
          );
        }

        // Create new items
        await Promise.all(
          lineItems.map((item) =>
            SalesOrderService.createItem({
              salesOrder: orderId,
              product: item.product.id,
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: item.discount,
              tax: item.tax,
              total: item.total,
            })
          )
        );
      }

      setTimeout(() => {
        navigate('/sales-orders');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving sales order:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save sales order. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Sales Order' : 'New Sales Order'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Header Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Order Number"
                  name="orderNumber"
                  value={formData.orderNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                />
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </Stack>

              <Autocomplete
                value={selectedCustomer}
                onChange={handleCustomerChange}
                options={customers}
                loading={customersLoading}
                getOptionLabel={(option) =>
                  option.attributes?.companyName || option.attributes?.contactPerson || ''
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer *"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customersLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                value={selectedQuotation}
                onChange={handleQuotationChange}
                options={quotations}
                loading={quotationsLoading}
                getOptionLabel={(option) =>
                  `${option.attributes?.quotationNumber} - ${
                    option.attributes?.customer?.data?.attributes?.companyName || ''
                  }`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From Quotation (Optional)"
                    helperText="Select to import items from an accepted quotation"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {quotationsLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Order Date"
                  name="orderDate"
                  type="date"
                  value={formData.orderDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Delivery Date"
                  name="deliveryDate"
                  type="date"
                  value={formData.deliveryDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="Expected delivery date"
                />
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Internal notes..."
              />
            </Stack>
          </Paper>

          {/* Line Items */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Line Items</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addLineItem}
                size="small"
              >
                Add Item
              </Button>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">Product</TableCell>
                    <TableCell width="10%">Qty</TableCell>
                    <TableCell width="15%">Unit Price</TableCell>
                    <TableCell width="12%">Discount</TableCell>
                    <TableCell width="12%">Tax</TableCell>
                    <TableCell width="15%">Total</TableCell>
                    <TableCell width="6%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No items added. Click "Add Item" to start.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item) => (
                      <TableRow key={item.tempId}>
                        <TableCell>
                          <Autocomplete
                            value={item.product}
                            onChange={(e, newValue) =>
                              updateLineItem(item.tempId, 'product', newValue)
                            }
                            options={products}
                            loading={productsLoading}
                            getOptionLabel={(option) =>
                              `${option.attributes?.name} (${option.attributes?.sku})`
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select product"
                                required
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.quantity}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'quantity', e.target.value)
                            }
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                            allowDecimal={false}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.unitPrice}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'unitPrice', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.discount}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'discount', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.tax}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'tax', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            OMR {item.total.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeLineItem(item.tempId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Totals Summary */}
          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="h6">OMR {formData.subtotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" color="error">
                    Discount:
                  </Typography>
                  <Typography variant="h6" color="error">
                    - OMR {formData.discount.toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Tax:</Typography>
                  <Typography variant="h6">OMR {formData.tax.toFixed(2)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h5">Total:</Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    OMR {formData.total.toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/sales-orders')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Order' : 'Create Order'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default SalesOrderForm;

